import { AlertCircle, Ban, Check, CheckCircle2, Clock, ExternalLink, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { approveBanner, disableBanner, fetchAdminBannerSummary, listAdminBanners, rejectBanner } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';

const STATUS_COLORS = {
  pending_approval: '#FFC20E',
  active: '#10B981',
  paused: '#9CA3AF',
  exhausted: '#9CA3AF',
  rejected: '#EF4444',
  disabled: '#EF4444',
};

const SummaryTile = ({ icon: Icon, label, value, testid }) => (
  <div
    className="rounded-xl p-3 md:p-4 border"
    style={{
      background: 'rgba(0,0,0,0.32)',
      borderColor: 'rgba(255, 182, 18, 0.18)',
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
      <Icon className="w-3.5 h-3.5 text-[#FFC20E]" />
      {label}
    </div>
    <div className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1.5 leading-none sc-gold-text">{value}</div>
  </div>
);

const BannerRow = ({ row, onApprove, onReject, onDisable, busyId }) => {
  const isPending = row.status === 'pending_approval';
  const isActiveOrPaused = row.status === 'active' || row.status === 'paused';
  const busy = busyId === row.id;
  return (
    <li
      className="rounded-xl border p-3 md:p-4 flex flex-col md:flex-row gap-4"
      style={{
        background: 'rgba(0,0,0,0.32)',
        borderColor: isPending ? 'rgba(255, 194, 14, 0.55)' : 'rgba(255, 182, 18, 0.18)',
      }}
      data-testid={`admin-banner-${row.id}`}
    >
      <div className="shrink-0 rounded-md overflow-hidden bg-black" style={{ width: '100%', maxWidth: 468, aspectRatio: '468 / 60' }}>
        <img
          src={row.image_url}
          alt=""
          width={468}
          height={60}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
      <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Owner</div>
          <div className="text-[12px] font-semibold mt-1 truncate">@{row.owner_username}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Status</div>
          <div className="text-[12px] font-semibold mt-1" style={{ color: STATUS_COLORS[row.status] || '#9CA3AF' }}>
            {row.status.replace(/_/g, ' ')}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Impressions</div>
          <div className="text-[12px] sc-tabular font-semibold mt-1 sc-gold-text">
            {row.impressions_served.toLocaleString()} / {row.impressions_purchased.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Clicks · CTR</div>
          <div className="text-[12px] sc-tabular font-semibold mt-1 sc-gold-text">
            {row.clicks.toLocaleString()} · {row.ctr_pct.toFixed(2)}%
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 shrink-0 min-w-[200px]">
        {isPending && (
          <>
            <button
              type="button"
              onClick={() => onApprove(row.id)}
              disabled={busy}
              className="sc-btn-white"
              style={{ padding: '8px 14px', fontSize: 11 }}
              data-testid={`admin-approve-${row.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Approve
            </button>
            <button
              type="button"
              onClick={() => onReject(row.id)}
              disabled={busy}
              className="sc-btn-ghost"
              style={{ padding: '8px 14px', fontSize: 11, borderColor: 'rgba(239,68,68,0.5)', color: '#FCA5A5' }}
              data-testid={`admin-reject-${row.id}`}
            >
              <X className="w-3 h-3" />
              Reject (refund)
            </button>
          </>
        )}
        {isActiveOrPaused && (
          <button
            type="button"
            onClick={() => onDisable(row.id)}
            disabled={busy}
            className="sc-btn-ghost"
            style={{ padding: '8px 14px', fontSize: 11, borderColor: 'rgba(239,68,68,0.5)', color: '#FCA5A5' }}
            data-testid={`admin-disable-${row.id}`}
          >
            <Ban className="w-3 h-3" />
            Disable
          </button>
        )}
        <a
          href={row.destination_url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Dest URL
        </a>
      </div>
    </li>
  );
};

const AdManagerSubpage = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [paging, setPaging] = useState({ page: 1, total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('pending_approval');
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const appliedDates = useDebouncedValue(dates);
  const [, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([fetchAdminBannerSummary(), listAdminBanners(page, filter || undefined, dateRangeParams(appliedDates))]);
      setSummary(s);
      setRows(Array.isArray(list?.rows) ? list.rows : []);
      setPaging({ page: list?.page || page, total: list?.total ?? 0, total_pages: list?.total_pages || 1 });
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [page, filter, appliedDates]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await approveBanner(id);
      toast.success('Banner approved — now in rotation');
      load();
    } catch (_err) {
      toast.error('Approve failed');
    } finally {
      setBusyId('');
    }
  };
  const handleReject = async (id) => {
    const reason = window.prompt('Optional rejection reason (shown to user):') || '';
    setBusyId(id);
    try {
      await rejectBanner(id, reason);
      toast.success('Banner rejected · credits refunded to user');
      load();
    } catch (_err) {
      toast.error('Reject failed');
    } finally {
      setBusyId('');
    }
  };
  const handleDisable = async (id) => {
    if (!window.confirm('Disable this banner? (no credit refund)')) return;
    setBusyId(id);
    try {
      await disableBanner(id);
      toast.success('Banner disabled');
      load();
    } catch (_err) {
      toast.error('Disable failed');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-6" data-testid="subpage-ad-manager">
      {/* Summary tiles */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-6" data-testid="ad-manager-summary">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Ad Manager · Overview</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <SummaryTile icon={Clock} label="Pending Review" value={summary ? summary.pending_count.toLocaleString() : '—'} testid="ad-manager-pending" />
          <SummaryTile icon={CheckCircle2} label="Active Banners" value={summary ? summary.active_count.toLocaleString() : '—'} testid="ad-manager-active" />
          <SummaryTile
            icon={ImageIcon}
            label="Total Impressions"
            value={summary ? summary.total_impressions_served.toLocaleString() : '—'}
            testid="ad-manager-impressions"
          />
          <SummaryTile
            icon={AlertCircle}
            label="Total Clicks · CTR"
            value={summary ? `${summary.total_clicks.toLocaleString()} · ${summary.average_ctr_pct.toFixed(2)}%` : '—'}
            testid="ad-manager-clicks"
          />
        </div>
      </section>

      {/* Filter pills */}
      <section className="sc-glass rounded-2xl p-3 md:p-4">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: 'pending_approval', label: 'Pending' },
            { id: 'active', label: 'Active' },
            { id: 'paused', label: 'Paused' },
            { id: 'exhausted', label: 'Exhausted' },
            { id: 'rejected', label: 'Rejected' },
            { id: 'disabled', label: 'Disabled' },
            { id: '', label: 'All' },
          ].map((f) => (
            <button
              key={f.id || 'all'}
              type="button"
              onClick={() => {
                setFilter(f.id);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition ${
                filter === f.id ? 'text-[#0a0a0b]' : 'text-[var(--sc-text-mute)] hover:text-[#FFC20E]'
              }`}
              style={{
                background: filter === f.id ? 'linear-gradient(135deg, #FFC20E, #FFB612)' : 'rgba(0,0,0,0.32)',
                borderColor: filter === f.id ? 'rgba(255,182,18,0.85)' : 'rgba(255,182,18,0.18)',
              }}
              data-testid={`ad-manager-filter-${f.id || 'all'}`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto">
            <AdminDateRange
              wrap
              dateFrom={dates.date_from}
              dateTo={dates.date_to}
              onChange={(range) => {
                setDates(range);
                setPage(1);
              }}
              fromLabel="Created From"
              toLabel="Created To"
              testIdPrefix="ad-manager-date"
            />
          </div>
        </div>
      </section>

      {/* Banner list */}
      <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="ad-manager-list">
        {rows.length === 0 ? (
          <div className="text-[12px] text-[var(--sc-text-mute)] text-center py-6">No banners match the current filter.</div>
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => (
              <BannerRow key={row.id} row={row} onApprove={handleApprove} onReject={handleReject} onDisable={handleDisable} busyId={busyId} />
            ))}
          </ul>
        )}
        <AdminPagination page={paging.page} totalPages={paging.total_pages} total={paging.total} onChange={setPage} />
      </section>
    </div>
  );
};

export default AdManagerSubpage;
